/**
 * Özgeçmiş düzenleyici — personanın yargısının kaynağı.
 *
 * Mizaç sekmesi *nasıl* tartıştığını ayarlar; bu bölüm hangi deneyimden
 * konuştuğunu. Backend her alanı bir EPİSTEMİK SONUCA çeviriyor: "yaş 58"
 * prompt'a sayı olarak değil, "mesleki formasyonun 1990'lar koşullarında
 * şekillendi, o dönemin dersleri senin için yaşanmış deneyim" olarak
 * giriyor. Bu yüzden formda her alanın yanında ne işe yaradığı yazıyor —
 * yoksa kullanıcı rastgele sayı girip etkisini anlamıyor.
 *
 * Cinsiyet BİLİNÇLİ olarak davranışa çevrilmiyor; yalnız hitap (Bey/Hanım),
 * TTS sesi ve avatar için var. Bunu arayüzde de açıkça söylüyoruz.
 */
import type {
  DemographicOptions,
  PersonaDemographics,
} from "../../domain/personaLibrary";
import { EMPTY_DEMOGRAPHICS, GENDER_LABELS } from "../../domain/personaLibrary";
import { Field, SectionTitle, Select, TextInput } from "./ui";

function TagListField({
  label,
  hint,
  placeholder,
  values,
  max,
  onChange,
}: {
  label: string;
  hint: string;
  placeholder: string;
  values: string[];
  max: number;
  onChange: (next: string[]) => void;
}) {
  // Virgülle ayrılmış tek input: ayrı bir "ekle" düğmesi olan chip editörü
  // bu yoğunluktaki formda gereksiz tıklama üretiyor.
  return (
    <Field label={label} hint={hint}>
      <TextInput
        value={values.join(", ")}
        placeholder={placeholder}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
              .slice(0, max),
          )
        }
      />
    </Field>
  );
}

export function DemographicsEditor({
  value,
  gender,
  options,
  onChange,
  onGenderChange,
}: {
  value: PersonaDemographics | null | undefined;
  gender: "M" | "F" | null;
  options: DemographicOptions | null;
  onChange: (next: PersonaDemographics) => void;
  onGenderChange: (next: "M" | "F" | null) => void;
}) {
  const d = { ...EMPTY_DEMOGRAPHICS, ...(value ?? {}) };
  const patch = (next: Partial<PersonaDemographics>) => onChange({ ...d, ...next });

  const ageMin = options?.ageRange.min ?? 22;
  const ageMax = options?.ageRange.max ?? 85;
  const expMax = options?.experienceMax ?? 60;
  // Sunucu deneyimi (yaş − 18) ile sınırlıyor; formda da aynı sınırı
  // uygulamak "kaydettim ama değer değişti" sürprizini önler.
  const expCeiling = d.age ? Math.min(expMax, Math.max(0, d.age - 18)) : expMax;

  return (
    <div className="space-y-4">
      <SectionTitle
        title="Özgeçmiş"
        hint="Boş bıraktığın her alan prompt'a hiçbir şey eklemez. Doldurduklarınsa personanın hangi deneyimden konuştuğunu belirler."
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label={d.age ? `Yaş: ${d.age}` : "Yaş (belirtilmemiş)"}
          hint="Mesleki formasyonunun hangi dönemde şekillendiğini belirler."
        >
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={ageMin}
              max={ageMax}
              value={d.age ?? 40}
              onChange={(e) => patch({ age: Number(e.target.value) })}
              className="flex-1 accent-violet-500"
            />
            {d.age !== null && (
              <button
                type="button"
                onClick={() => patch({ age: null })}
                className="shrink-0 rounded px-1.5 py-0.5 text-[10.5px] text-slate-500 hover:text-rose-400"
                title="Temizle"
              >
                ✕
              </button>
            )}
          </div>
        </Field>

        <Field
          label={
            d.experienceYears !== null
              ? `Mesleki Deneyim: ${d.experienceYears} yıl`
              : "Mesleki Deneyim (belirtilmemiş)"
          }
          hint="Kaç tam teknoloji/politika döngüsü gördüğünü belirler."
        >
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={expCeiling}
              value={Math.min(d.experienceYears ?? 10, expCeiling)}
              onChange={(e) => patch({ experienceYears: Number(e.target.value) })}
              className="flex-1 accent-violet-500"
            />
            {d.experienceYears !== null && (
              <button
                type="button"
                onClick={() => patch({ experienceYears: null })}
                className="shrink-0 rounded px-1.5 py-0.5 text-[10.5px] text-slate-500 hover:text-rose-400"
                title="Temizle"
              >
                ✕
              </button>
            )}
          </div>
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Kıdem Kademesi" hint="Uygulama detayında mı, stratejik çerçevede mi konuşur.">
          <Select
            value={d.seniority ?? ""}
            onChange={(e) => patch({ seniority: e.target.value || null })}
          >
            <option value="">— Belirtilmemiş —</option>
            {(options?.seniority ?? []).map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Eğitim Düzeyi" hint="Hangi kanıt türünü ikna edici bulduğunu belirler.">
          <Select
            value={d.education ?? ""}
            onChange={(e) => patch({ education: e.target.value || null })}
          >
            <option value="">— Belirtilmemiş —</option>
            {(options?.education ?? []).map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Eğitim Alanı" hint="Sorunu öncelikle hangi disiplinin kavramlarıyla çerçeveler.">
          <TextInput
            value={d.educationField ?? ""}
            maxLength={80}
            placeholder="ör. Makine Mühendisliği"
            onChange={(e) => patch({ educationField: e.target.value || null })}
          />
        </Field>

        <Field label="Yetiştiği Sektör" hint="Kurumsal içgüdülerinin şekillendiği kültür.">
          <Select
            value={d.backgroundSector ?? ""}
            onChange={(e) => patch({ backgroundSector: e.target.value || null })}
          >
            <option value="">— Belirtilmemiş —</option>
            {(options?.backgroundSector ?? []).map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Bakış Ölçeği" hint="Hangi mevzuat/pazar çerçevesini varsayılan alır.">
          <Select
            value={d.regionScope ?? ""}
            onChange={(e) => patch({ regionScope: e.target.value || null })}
          >
            <option value="">— Belirtilmemiş —</option>
            {(options?.regionScope ?? []).map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Görev Yeri">
          <TextInput
            value={d.location ?? ""}
            maxLength={80}
            placeholder="ör. Ankara"
            onChange={(e) => patch({ location: e.target.value || null })}
          />
        </Field>
      </div>

      <TagListField
        label="Yetkinlik Belgeleri"
        hint="Virgülle ayır. Kapsamına giren konularda daha bağlayıcı konuşur."
        placeholder="ör. PMP, CISSP, ISO 27001 Baş Denetçi"
        values={d.certifications}
        max={6}
        onChange={(certifications) => patch({ certifications })}
      />

      <TagListField
        label="Kariyerinde Yer Aldığı İşler"
        hint="Virgülle ayır. Bunlara atıf yapabilir; yer almadığı bir işi kendine mal etmesi prompt'ta yasaklanır."
        placeholder="ör. MİLGEM tedarik denetimi, AESA radar yerlileştirme"
        values={d.careerHighlights}
        max={4}
        onChange={(careerHighlights) => patch({ careerHighlights })}
      />

      <TagListField
        label="Bildiği Diller"
        hint="Virgülle ayır. Yalnız kaynak erişimini etkiler; çıktı her hâlükârda Türkçe."
        placeholder="ör. İngilizce, Almanca"
        values={d.languages}
        max={5}
        onChange={(languages) => patch({ languages })}
      />

      <div className="border-t border-white/[0.07] pt-3">
        <Field
          label="Cinsiyet"
          hint="Yalnız hitap (Bey/Hanım), seslendirme ve avatar için kullanılır. Personanın davranışını ETKİLEMEZ — bundan tutum türetmek ne doğru ne de savunulabilir olurdu."
        >
          <Select
            value={gender ?? ""}
            onChange={(e) => onGenderChange((e.target.value || null) as "M" | "F" | null)}
          >
            <option value="">— Belirtilmemiş —</option>
            <option value="M">{GENDER_LABELS.M}</option>
            <option value="F">{GENDER_LABELS.F}</option>
          </Select>
        </Field>
      </div>
    </div>
  );
}
